import React from 'react'

import { LayerChangeIcon } from '../../changeIcon'
import { describeOnlyIn, listChanges } from '../../describeChange'
import { LayerChange } from '../../layerTypes'
import * as Styled from './ChangesPanel.styles'

interface LayerRowProps {
  change: LayerChange
  isSelected: boolean
  /** It is only in the version the canvas is not showing. */
  isFromComparedVersion: boolean
  onSelect(): void
}

/**
 * One changed layer, wherever it is listed.
 *
 * The same row whether it sits under the frame that holds it or in the group
 * for the layers that belong to no frame at all - a change is the same thing to
 * point at and click either way.
 */
export function LayerRow({
  change,
  isSelected,
  isFromComparedVersion,
  onSelect,
}: LayerRowProps) {
  return (
    <Styled.ChangeButton
      type="button"
      $nested
      $selected={isSelected}
      $compared={isFromComparedVersion}
      aria-pressed={isSelected}
      onClick={onSelect}
    >
      <Styled.RowIcon
        aria-hidden
        $selected={isSelected}
        $compared={isFromComparedVersion}
      >
        <LayerChangeIcon change={change} />
      </Styled.RowIcon>
      <Styled.Text>
        <Styled.Name $selected={isSelected} $compared={isFromComparedVersion}>
          {change.name || 'Untitled'}
        </Styled.Name>
        {/* One per line: a layer that moved, was resized and repainted says
        three things, and the version it is only in says one whole one. */}
        {change.kind === 'modified' ? (
          // By position: the list is rebuilt whole from the change, so a
          // phrase never moves between lines without its line changing too
          listChanges(change.changes).map((what, index) => (
            <Styled.What key={index} $bulleted data-testid="layer-change">
              {what}
            </Styled.What>
          ))
        ) : (
          <Styled.What data-testid="layer-change">
            {describeOnlyIn(change.kind)}
          </Styled.What>
        )}
      </Styled.Text>
    </Styled.ChangeButton>
  )
}
